/**
 * Client for /api/notices (backend/chainlit/notices_api.py).
 *
 * One shared store, because two surfaces read it: the bell's popover in the
 * header and the /notifications page. Marking a card read in one must show in
 * the other without a second request.
 *
 * The badge is polled every few minutes and on window focus. The server
 * decides what a card says; nothing here composes wording.
 */
import { useEffect, useSyncExternalStore } from 'react';

export type NoticeAction =
  | { type: 'reports' }
  | { type: 'email'; report_id: number; title?: string }
  | { type: 'route'; to: string }
  | { type: 'renew' }
  | { type: 'topics'; topic?: string };

export interface Notice {
  id: number;
  kind: string;
  level: 'attention' | 'action' | 'info';
  state: 'open' | 'ended' | 'info';
  at: string | null;
  unread: boolean;
  unseen: boolean;
  dismissible: boolean;
  title: string;
  /** The report's own name, on a line of its own: it may be Arabic. */
  subject: string | null;
  body: string;
  cta: { label: string; action: NoticeAction } | null;
}

interface State {
  items: Notice[] | null; // null until the list was read once
  unseen: number;
  loading: boolean;
}

const POLL_MS = 5 * 60_000;
const JSON_HEADERS = { 'Content-Type': 'application/json' };

let state: State = { items: null, unseen: 0, loading: false };
const listeners = new Set<() => void>();

function set(patch: Partial<State>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export const useNotices = () => useSyncExternalStore(subscribe, () => state);

let countInflight: Promise<void> | null = null;

export function refreshCount(): Promise<void> {
  if (countInflight) return countInflight;
  countInflight = fetch('/api/notices/count', { credentials: 'include' })
    .then((r) => (r.ok ? r.json() : null))
    .then((d: { unseen: number } | null) => {
      if (d) set({ unseen: d.unseen });
    })
    .catch(() => undefined)
    .finally(() => {
      countInflight = null;
    });
  return countInflight;
}

let listInflight: Promise<void> | null = null;

/** Re-read the cards. A failure keeps what we last had. */
export function refreshNotices(): Promise<void> {
  if (listInflight) return listInflight;
  set({ loading: true });
  listInflight = fetch('/api/notices', { credentials: 'include' })
    .then((r) => (r.ok ? r.json() : null))
    .then((d: { items: Notice[]; unseen: number } | null) => {
      if (d) set({ items: d.items, unseen: d.unseen });
    })
    .catch(() => undefined)
    .finally(() => {
      listInflight = null;
      set({ loading: false });
    });
  return listInflight;
}

const post = (url: string, body?: unknown) =>
  fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: JSON_HEADERS,
    body: body === undefined ? undefined : JSON.stringify(body)
  });

/** The bell was opened: the badge goes to zero, the cards stay highlighted. */
export function markSeen() {
  if (!state.unseen && !state.items?.some((i) => i.unseen)) return;
  set({
    unseen: 0,
    items: state.items?.map((i) => ({ ...i, unseen: false })) ?? null
  });
  post('/api/notices/seen').catch(() => undefined);
}

export function markRead(ids: number[] | 'all') {
  const all = ids === 'all';
  const hit = (n: Notice) => all || (ids as number[]).includes(n.id);
  if (!state.items?.some((n) => hit(n) && n.unread)) return;
  set({
    items:
      state.items?.map((n) =>
        hit(n) ? { ...n, unread: false, unseen: false } : n
      ) ?? null
  });
  post('/api/notices/read', all ? { all: true } : { ids }).catch(
    () => undefined
  );
}

export function dismiss(id: number) {
  set({ items: state.items?.filter((n) => n.id !== id) ?? null });
  post(`/api/notices/${id}/dismiss`).catch(() => undefined);
}

/** Keeps the badge fresh while the app is open. Mount once (the bell). */
export function useNoticePolling(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    refreshCount();
    const timer = window.setInterval(refreshCount, POLL_MS);
    const onFocus = () => refreshCount();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled]);
}

/** "10:05 AM" today, "Yesterday", else "8 Oct". */
export function whenLabel(iso: string | null, now = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86_400_000);
  if (diff === 0)
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export const isToday = (iso: string | null, now = new Date()) =>
  !!iso && new Date(iso).toDateString() === now.toDateString();
